import React from 'react';
import { withTranslation } from 'react-i18next';
import { ProductService } from '../../services';
import { DefaultButton, LoadingButton } from '../buttons';
import { TextField, Checkbox } from '../fields';
import { Confirm, Toast } from '../alerts';
import { ModalLayout } from '../layouts';

import '../../styles/modals/modal.css';

/** Roughly how long one image takes, used only for the time estimate shown before starting. */
const SECONDS_PER_IMAGE = 15;

const estimate = (count) => {
  const total = count * SECONDS_PER_IMAGE;
  if (total < 90) return `${total} seconds`;
  return `${Math.round(total / 60)} minutes`;
};

/** Generates product photos in bulk, one request per product so progress survives and nothing times out. */
class GenerateImagesModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.initialState();
    this.productService = new ProductService();
    this.cancelled = false;
  }

  initialState() {
    return { show: false, venue: null, products: [], selected: [], search: '', status: {}, photos: {}, running: false, error: null };
  }

  componentDidMount() {
    if (this.props.onRef) this.props.onRef(this);
  }

  componentWillUnmount() {
    if (this.props.onRef) this.props.onRef(undefined);
  }

  /** Products come from the page so we reuse the list already loaded, photos included. */
  show(show, venue = null, products = []) {
    const list = (products || []).filter((product) => !product.archived);
    this.cancelled = false;
    this.setState({
      ...this.initialState(),
      show,
      venue,
      products: list,
      selected: list.filter((product) => !product.photo).map((product) => product._id),
    });
  }

  errorFrom(e, fallback) {
    return (e && e.errors && e.errors[0]) || (e && e.message) || fallback;
  }

  toggle(id, checked) {
    const ids = this.state.selected.filter((x) => x !== id);
    this.setState({ selected: checked ? [...ids, id] : ids });
  }

  selectShown(checked) {
    const shown = this.shown().map((product) => product._id);
    if (checked) {
      const merged = this.state.selected.filter((id) => !shown.includes(id));
      this.setState({ selected: [...merged, ...shown] });
    } else {
      this.setState({ selected: this.state.selected.filter((id) => !shown.includes(id)) });
    }
  }

  selectMissing() {
    this.setState({ selected: this.state.products.filter((product) => !product.photo && !this.state.photos[product._id]).map((product) => product._id) });
  }

  shown() {
    const search = this.state.search.trim().toLowerCase();
    return search ? this.state.products.filter((product) => product.name.toLowerCase().includes(search)) : this.state.products;
  }

  failedIds() {
    return Object.keys(this.state.status).filter((id) => this.state.status[id] === 'FAILED');
  }

  run = async (ids = null) => {
    const { t } = this.props;
    const targets = this.state.products.filter((product) => (ids || this.state.selected).includes(product._id));
    if (!targets.length) return;

    const replacing = targets.filter((product) => product.photo && !this.state.photos[product._id]).length;
    if (replacing) {
      const ok = await Confirm.open(
        t('Replace existing images'),
        `${replacing} ${replacing === 1 ? t('of these products already has a photo') : t('of these products already have photos')}. ${t('Generating will replace them. Continue?')}`,
        t('Yes'),
        t('No')
      );
      if (!ok) return;
    }

    this.cancelled = false;
    this.setState({ running: true, error: null });

    let done = 0;
    let failed = 0;

    for (const product of targets) {
      if (this.cancelled) break;
      this.setState((s) => ({ status: { ...s.status, [product._id]: 'RUNNING' } }));

      try {
        const result = await this.productService.generateImage(product._id);
        if (result && result.success) {
          done++;
          this.setState((s) => ({ status: { ...s.status, [product._id]: 'DONE' }, photos: { ...s.photos, [product._id]: result.photo } }));
        } else {
          failed++;
          this.setState((s) => ({ status: { ...s.status, [product._id]: 'FAILED' }, error: this.errorFrom(result, s.error) }));
        }
      } catch (e) {
        failed++;
        this.setState((s) => ({ status: { ...s.status, [product._id]: 'FAILED' }, error: this.errorFrom(e, s.error) }));
      }
    }

    this.setState({ running: false });

    const stopped = targets.length - done - failed;
    if (this.cancelled && stopped > 0) Toast.warning(`${done} ${t('generated')}, ${stopped} ${t('not started')}`);
    else if (failed) Toast.warning(`${done} ${t('generated')}, ${failed} ${t('failed')}`);
    else Toast.success(`${done} ${done === 1 ? t('image generated') : t('images generated')}`);

    if (done && this.props.onComplete) this.props.onComplete();
  };

  stop = () => {
    this.cancelled = true;
  };

  close = () => {
    if (this.state.running) return;
    this.setState({ show: false });
  };

  mark(product) {
    const { t } = this.props;
    const state = this.state.status[product._id];
    if (state === 'RUNNING') return t('generating');
    if (state === 'DONE') return t('done');
    if (state === 'FAILED') return t('failed');
    if (this.state.photos[product._id] || product.photo) return t('has image');
    return '';
  }

  render() {
    const { t } = this.props;
    const { show, venue, products, selected, search, status, photos, running, error } = this.state;
    const shown = this.shown();
    const shownIds = shown.map((product) => product._id);
    const allShownSelected = shownIds.length > 0 && shownIds.every((id) => selected.includes(id));
    const failed = this.failedIds();
    const completed = Object.keys(status).filter((id) => status[id] === 'DONE').length;

    return (
      show && (
        <ModalLayout title={t('Generate Images')} subtitle={venue ? venue.name : null} width={640}>
          <p className='add-venue-subtitle' style={{ marginTop: 16 }}>
            {selected.length} {selected.length === 1 ? t('product selected') : t('products selected')}
            {selected.length > 0 ? `, ${t('about')} ${estimate(selected.length)}` : ''}
          </p>

          <TextField fullWidth label={t('Search products')} value={search} onChange={(e) => this.setState({ search: e.target.value })} />

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 24px', marginTop: 8 }}>
            <Checkbox text={t('Select all shown')} value={true} checked={allShownSelected} onChange={(checked) => !running && this.selectShown(checked)} />
            <Checkbox text={t('Only ones without an image')} value={true} checked={false} onChange={() => !running && this.selectMissing()} />
          </div>

          <div style={{ maxHeight: 300, overflowY: 'auto', marginTop: 8 }}>
            {shown.map((product) => {
              const photo = photos[product._id] || product.photo;
              const note = this.mark(product);

              return (
                <div key={product._id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Checkbox
                      text={product.name}
                      value={product._id}
                      checked={selected.includes(product._id)}
                      onChange={(checked) => !running && this.toggle(product._id, checked)}
                    />
                  </div>
                  {note && <span className='add-venue-subtitle' style={{ margin: 0, whiteSpace: 'nowrap' }}>{note}</span>}
                  {photo && <img src={photo} alt='' style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'cover' }} />}
                </div>
              );
            })}
            {products.length > 0 && shown.length < 1 && <p>{t('No products match that search.')}</p>}
            {products.length < 1 && <p>{t('This venue has no products yet.')}</p>}
          </div>

          {running && (
            <p className='add-venue-subtitle' style={{ marginTop: 16 }}>
              {completed} / {selected.length} {t('complete')}
            </p>
          )}

          {error && (
            <span className='modal-error-text' style={{ marginTop: 14 }}>
              {error}
            </span>
          )}

          <div className='modal-buttons-container'>
            <div className='modal-button'>
              <LoadingButton
                text={t('GENERATE')}
                state={running ? 'LOADING' : 'DONE'}
                onClick={() => !running && selected.length > 0 && this.run()}
                fullWidth
              />
            </div>
            <div className='modal-button'>
              {running ? <DefaultButton text={t('STOP')} onClick={this.stop} fullWidth /> : <DefaultButton text={t('CLOSE')} onClick={this.close} fullWidth />}
            </div>
          </div>

          {!running && failed.length > 0 && (
            <div className='modal-buttons-container' style={{ marginTop: 12 }}>
              <div className='modal-button-full'>
                <DefaultButton text={`${t('RETRY FAILED')} (${failed.length})`} onClick={() => this.run(failed)} fullWidth />
              </div>
            </div>
          )}
        </ModalLayout>
      )
    );
  }
}

export default withTranslation()(GenerateImagesModal);
